/**
 * REST client for Smart Waitlist.
 */

import { httpRequest } from "@/lib/axios";

export type WaitlistEntry = {
  id: string;
  patient_id: string;
  employee_id: string | null;
  procedure_id: string | null;
  preferred_dates: string[] | null;
  preferred_times: ("morning" | "afternoon" | "evening")[] | null;
  status: "waiting" | "notified" | "scheduled" | "expired";
  notified_at: string | null;
  notes: string | null;
  created_at: string;
  patient?: { id: string; person?: { name: string; phone?: string } };
};

export type WaitlistAvailability = {
  waitlist_id: string;
  patient_name: string;
  available_slot: {
    date: string;
    time: string;
    employee_name: string;
  };
};

/**
 * P-07: the reads propagate their failure. Catching it here handed the page an
 * empty list, which it drew as "nobody is waiting" — the one sentence a clinic
 * with ten patients waiting must never read (PRODUCT_GOVERNANCE rule 8). The
 * writes keep returning null/false: a toast reports them next to the action.
 */
export async function fetchWaitlist(): Promise<WaitlistEntry[]> {
  const { data } = await httpRequest.get("/waitlist");
  if (!Array.isArray(data)) throw new Error("Invalid waitlist response");
  return data;
}

export async function addToWaitlist(payload: {
  patient_id: string;
  employee_id?: string;
  procedure_id?: string;
  preferred_dates?: string[];
  preferred_times?: string[];
  notes?: string;
}): Promise<WaitlistEntry | null> {
  try {
    const { data } = await httpRequest.post("/waitlist", payload);
    return data;
  } catch {
    return null;
  }
}

export async function removeFromWaitlist(id: string): Promise<boolean> {
  try {
    await httpRequest.delete(`/waitlist/${id}`);
    return true;
  } catch {
    return false;
  }
}

export async function checkWaitlistAvailability(): Promise<WaitlistAvailability[]> {
  const { data } = await httpRequest.get("/waitlist/check-availability");
  if (!Array.isArray(data.matches)) throw new Error("Invalid waitlist availability response");
  return data.matches.map((match: { waitlist_entry: WaitlistEntry; available_slot: { date: string; hour: string } }) => ({
    waitlist_id: match.waitlist_entry.id,
    patient_name: match.waitlist_entry.patient?.person?.name ?? "Nome indisponível",
    available_slot: { date: match.available_slot.date, time: match.available_slot.hour, employee_name: "" },
  }));
}

export async function notifyWaitlistPatient(id: string): Promise<boolean> {
  try {
    await httpRequest.post(`/waitlist/${id}/notify`);
    return true;
  } catch {
    return false;
  }
}
